import { useEffect, useState } from 'react';
import { updatePageSEO } from '../../utils/seo';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function CookiePolicy() {
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [showBanner, setShowBanner] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    updatePageSEO({
      title: 'Cookie Policy - Market Me | How We Use Cookies & Tracking',
      description: 'Understand how Market Me uses cookies and similar technologies to improve your browsing experience. Learn about cookie types, purposes, and how to manage your preferences.',
      keywords: 'cookie policy, cookies, tracking, website cookies, Market Me cookies',
      canonical: 'https://marketme.com/cookie-policy'
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if user has already set preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setCookiePreferences(JSON.parse(savedPreferences));
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setSaveMessage('Your cookie preferences have been saved successfully.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    setSaveMessage('All cookies have been accepted.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setCookiePreferences(essentialOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    setShowBanner(false);
    setSaveMessage('Only essential cookies are enabled.');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  return (
    <div className="relative min-h-screen bg-black text-white">
      {/* Fixed Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-black"></div>
        <div className="absolute inset-0 opacity-100">
          <div 
            className="absolute inset-0 animate-flow-slow"
            style={{
              background: 'radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.12) 0%, transparent 50%)',
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10">
        <Navigation />
        
        <div className="max-w-[900px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-white/60 mb-8 text-sm md:text-base">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          {/* Cookie Preferences Manager */}
          <div className="bg-[#1a1a1a] rounded-2xl p-6 md:p-8 mb-8 border border-white/10">
            <h2 className="text-2xl font-semibold text-white mb-4">Manage Your Cookie Preferences</h2>
            <p className="text-white/70 mb-6">
              You can control which cookies we use on our website. Essential cookies cannot be disabled as they are necessary for the website to function.
            </p>

            <div className="space-y-4 mb-6">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-xl">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">Essential Cookies</h3>
                  <p className="text-sm text-white/60">Required for the website to function properly</p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-[#8b2bd9] rounded-full flex items-center px-1 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full ml-auto"></div>
                  </div>
                  <p className="text-xs text-white/40 mt-1">Always On</p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-xl">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">Analytics Cookies</h3>
                  <p className="text-sm text-white/60">Help us understand how visitors use our website</p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setCookiePreferences({ ...cookiePreferences, analytics: !cookiePreferences.analytics })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                      cookiePreferences.analytics ? 'bg-[#8b2bd9]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full transition-all ${
                      cookiePreferences.analytics ? 'ml-auto' : 'ml-0'
                    }`}></div>
                  </button>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-4 bg-[#0a0a0a] rounded-xl">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">Marketing Cookies</h3>
                  <p className="text-sm text-white/60">Used to deliver relevant advertisements</p>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setCookiePreferences({ ...cookiePreferences, marketing: !cookiePreferences.marketing })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors cursor-pointer ${
                      cookiePreferences.marketing ? 'bg-[#8b2bd9]' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full transition-all ${
                      cookiePreferences.marketing ? 'ml-auto' : 'ml-0'
                    }`}></div>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 h-12 bg-[#8b2bd9] text-white font-semibold rounded-xl hover:bg-[#7a26c1] transition-all cursor-pointer whitespace-nowrap"
              >
                Save Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 h-12 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                Accept All
              </button>
              <button
                onClick={handleRejectNonEssential}
                className="flex-1 h-12 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                Reject Non-Essential
              </button>
            </div>

            {saveMessage && (
              <div className="mt-4 p-3 bg-[#8b2bd9]/20 border border-[#8b2bd9]/40 rounded-lg text-center text-white/90 text-sm">
                {saveMessage}
              </div>
            )}
          </div>

          <div className="space-y-8 text-white/80 leading-relaxed">
            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">1. What Are Cookies?</h2>
              <p className="mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
              </p>
              <p>
                Cookies help us understand how you use our website, remember your preferences, and improve your overall experience.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
              <p className="mb-4">Market Me uses cookies for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>To enable essential website functionality</li>
                <li>To remember your preferences and settings</li>
                <li>To analyse website traffic and user behaviour</li>
                <li>To improve website performance and user experience</li>
                <li>To provide personalised content and features</li>
                <li>To measure the effectiveness of our marketing campaigns</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.1 Essential Cookies (Always Active)</h3>
              <p className="mb-4">
                These cookies are necessary for the website to function and cannot be disabled. They enable core functionality such as security, network management, and accessibility.
              </p>
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-white">Cookie Name</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70">
                    <tr className="border-b border-white/5">
                      <td className="py-2">cookiePreferences</td>
                      <td className="py-2">Stores your cookie consent preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">sessionID</td>
                      <td className="py-2">Maintains your session state</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr>
                      <td className="py-2">security_token</td>
                      <td className="py-2">Protects against cross-site request forgery</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.2 Analytics Cookies (Optional)</h3>
              <p className="mb-4">
                These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use this data to improve our website and services.
              </p>
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-white">Cookie Name</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70">
                    <tr className="border-b border-white/5">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr>
                      <td className="py-2">_gat</td>
                      <td className="py-2">Google Analytics - throttles request rate</td>
                      <td className="py-2">1 minute</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">3.3 Marketing Cookies (Optional)</h3>
              <p className="mb-4">
                These cookies track your online activity to help advertisers deliver more relevant advertising or limit how many times you see an advertisement. They may be set by us or third-party providers.
              </p>
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 text-white">Cookie Name</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70">
                    <tr className="border-b border-white/5">
                      <td className="py-2">_fbp</td>
                      <td className="py-2">Facebook Pixel - tracks conversions</td>
                      <td className="py-2">3 months</td>
                    </tr>
                    <tr>
                      <td className="py-2">ads_prefs</td>
                      <td className="py-2">Stores advertising preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">4. Third-Party Cookies</h2>
              <p className="mb-4">
                Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.1 Google Analytics</h3>
              <p className="mb-4">
                We use Google Analytics to analyse website usage. Google Analytics uses cookies to collect information about how visitors use our site. This information is used to compile reports and help us improve the website.
              </p>
              <p className="mb-4">
                For more information: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">Google Privacy Policy</a>
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.2 Social Media Platforms</h3>
              <p className="mb-4">
                Our website may include social media features (e.g., share buttons). These features may set cookies to enable them to function properly. Your interactions with these features are governed by the privacy policies of the companies providing them.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">4.3 Chat Widget</h3>
              <p>
                Our chat widget may use cookies to maintain conversation state and provide a better user experience. These cookies are essential for the chat functionality to work properly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">5. How to Control Cookies</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.1 Cookie Preference Manager</h3>
              <p className="mb-4">
                You can manage your cookie preferences using the Cookie Preference Manager at the top of this page. Your choices will be saved and applied across our website.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">5.2 Browser Settings</h3>
              <p className="mb-4">
                Most web browsers allow you to control cookies through their settings. You can set your browser to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Block all cookies</li>
                <li>Block third-party cookies only</li>
                <li>Delete cookies when you close your browser</li>
                <li>Notify you when a website tries to set a cookie</li>
              </ul>
              <p className="mb-4">Browser-specific instructions:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#8b2bd9] hover:underline">Microsoft Edge</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">6. Impact of Disabling Cookies</h2>
              <p className="mb-4">
                If you disable cookies, some features of our website may not function properly. Specifically:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You may need to re-enter information more frequently</li>
                <li>Some personalisation features may not work</li>
                <li>Certain interactive features may be unavailable</li>
                <li>We won't be able to remember your preferences</li>
              </ul>
              <p className="mt-4">
                Essential cookies cannot be disabled as they are necessary for the website to function.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">7. Cookie Duration</h2>
              <p className="mb-4">Cookies can be classified by duration:</p>
              
              <h3 className="text-xl font-semibold text-white mb-3 mt-6">7.1 Session Cookies</h3>
              <p className="mb-4">
                Temporary cookies that are deleted when you close your browser. They help maintain your session as you navigate through our website.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">7.2 Persistent Cookies</h3>
              <p>
                Cookies that remain on your device for a set period or until you delete them. They remember your preferences and settings for future visits.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">8. Updates to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in technology, legislation, or our business practices. We will notify you of significant changes by posting the updated policy on our website with a new "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">9. Your Rights</h2>
              <p className="mb-4">
                Under UK GDPR, you have rights regarding your personal data, including data collected through cookies:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Right to access your data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
              </ul>
              <p className="mt-4">
                For more information about your rights, please see our <a href="/privacy-policy" className="text-[#8b2bd9] hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4">10. Contact Us</h2>
              <p className="mb-4">
                If you have questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <p>
                <strong>Email:</strong> team.marketme@gmail.com
              </p>
            </section>
          </div>
        </div>

        <Footer />
      </div>

      {/* Cookie Consent Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#1a1a1a] border-t border-white/10 p-4 md:p-6 z-50">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2">We Use Cookies</h3>
                <p className="text-sm text-white/70">
                  We use cookies to enhance your browsing experience, analyse site traffic, and provide personalised content. By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={handleRejectNonEssential}
                  className="h-10 px-6 bg-white/10 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={() => setShowBanner(false)}
                  className="h-10 px-6 bg-white/10 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="h-10 px-6 bg-[#8b2bd9] text-white text-sm font-semibold rounded-lg hover:bg-[#7a26c1] transition-all cursor-pointer whitespace-nowrap"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
